export const TREATMENT_CHECKOUT_COPY = Object.freeze({
  title: "Confirm and authorize",
  description:
    "Review the treatment and lab charges below, then authorize payment to submit your care request.",
  paymentHeading: "Payment method",
  authorization:
    "Your payment authorizes the exact total shown for this immutable treatment release. Any clinically required prescription change is reviewed before a different amount can be captured.",
  authorizationHeading: "How payment works",
  authorizationSteps: Object.freeze([
    "Medication amounts are authorized from this accepted quote. They are settled from the provider's complete prescribed set.",
    "A denial, revised prescription, or lower final amount follows the recorded settlement and refund workflow.",
  ]),
  labAuthorization:
    "Selected labs are separate lab orders and are captured independently. Required final results must be available before the affected treatment is released for provider review.",
  compliance:
    "By authorizing, you confirm the payment and consent information above and agree to the configured care and privacy terms.",
  products: "Treatment",
  supplies: "Included supplies",
  labs: "Labs charged today",
  subtotal: "Products",
  shipping: "Shipping",
  discount: "Discount",
  total: "Due today",
  button: "Authorize and submit",
  processing: "Authorizing…",
});

export const TREATMENT_CHECKOUT_ERROR_COPY = Object.freeze({
  // R4: the affected treatment comes from error details, never the copy.
  treatment_configuration_unavailable:
    "This treatment is temporarily unavailable.",
  treatment_type_duplicate_program:
    "Please choose one of these treatments to continue.",
  treatment_case_set_inconsistent:
    "Your order details changed. Please review and try again.",
  // A cart total that cannot be reconciled reuses the existing
  // allocation_mismatch code rather than minting an R4 duplicate.
  allocation_mismatch:
    "Your total changed. Please review the latest pricing.",
  required_answers_missing: "Please answer this question to continue.",
  answer_type_invalid: "Please review this answer and try again.",
  care_location_invalid:
    "Please choose the state where you will receive care.",
  care_location_unavailable:
    "This treatment is not available in the state you selected.",
  product_unavailable_for_care_location:
    "A selected treatment option is not available in your care location.",
  product_selection_stale:
    "A previously selected treatment option is no longer available. Review your choices.",
  runtime_patient_mismatch:
    "This session belongs to a different account. Please sign in again.",
  runtime_revision_stale:
    "Your session was refreshed. Please continue.",
  stale_revision:
    "Your session was refreshed. Please continue.",
  selection_minimum_not_met: "Select the required number of Products to continue.",
  selection_maximum_exceeded: "Remove a Product before continuing.",
  product_selection_required: "Choose one treatment option in each required section.",
  selection_role_not_selectable:
    "That option cannot be chosen here. Review your treatment choices.",
  selection_quantity_invalid:
    "That quantity is not available for this option. Choose an offered quantity.",
  selection_duration_invalid:
    "That supply duration is not available for this option. Choose an offered duration.",
  coupon_invalid: "That discount code is not valid.",
  coupon_expired: "That discount code has expired.",
  coupon_usage_exhausted: "That discount code is no longer available.",
  coupon_not_eligible: "That discount code is not available on this account.",
  coupon_min_spend: "That discount code needs a higher order total to apply.",
  coupon_no_eligible_items:
    "That discount code does not apply to the items you selected.",
  invalid_selection_limits: "This Product selection is temporarily unavailable. Please refresh and try again.",
  product_unavailable: "One of the selected Products is no longer available. Review your selections and try again.",
  product_state_unavailable: "One of the selected Products is not available in your service state.",
  shipping_location_invalid: "Please confirm or correct the address where your order will ship.",
  no_fulfillable_products:
    "No treatment options can ship to this address. Please change your shipping address or contact the care team.",
  treatment_product_mismatch: "The selected Product is not available for this treatment.",
  invalid_program_stage: "This treatment release is no longer available.",
  missing_program_visit_type: "This treatment release is missing its visit configuration.",
  program_visit_type_mismatch: "The treatment visit configuration changed. Refresh and try again.",
  treatment_authorization_failed: "Payment was declined. Your selections are preserved so you can update payment details.",
  payment_pending: "Payment confirmation is pending. Do not submit again while we reconcile the existing authorization.",
  checkout_submission_replayed: "This checkout was already submitted. Your existing result is being restored.",
});

export const TREATMENT_COMPLETION_COPY = Object.freeze({
  title: "Order submitted",
  previewTitle: "Preview completed",
  description:
    "Your care request was submitted. A licensed provider will review each treatment before anything is prescribed or fulfilled.",
  previewDescription:
    "This is the confirmation patients see. Preview mode did not create a patient, payment, order, lab order, provider dispatch, or reimbursement.",
  orders: "Treatment orders",
  labs: "Lab orders",
  payment: "Payment authorization",
  next:
    "Clinical review happens next. Order and lab statuses can be followed from the patient portal.",
  previewNext:
    "In a live journey, clinical review starts next and patients can follow each order from their portal.",
  nextSteps: Object.freeze([
    "A licensed provider reviews each treatment independently.",
    "Each treatment is charged only when its own provider approves it, so one may be charged and shipped while another is still under review.",
    "If a treatment is declined, its authorization hold is released and nothing is charged for it. Your other treatments are unaffected.",
    "Order, lab, payment, refund, and recovery statuses remain available in the patient portal.",
  ]),
  important:
    "A provider may revise or deny a treatment. Any resulting charge, supplemental authorization, refund-required state, or manual recovery follows the accepted quote and recorded prescription facts.",
  viewOrders: "View my orders",
});

export const TREATMENT_PATIENT_PORTAL_PATH = Object.freeze({
  orders: "/dashboard/orders",
});

export const TREATMENT_TERMINAL_STEP = Object.freeze({
  checkout: Object.freeze({
    id: "terminal:checkout",
    kind: "checkout",
    context: "Authorize payment",
    sectionName: "Checkout",
  }),
  completion: Object.freeze({
    id: "terminal:completion",
    kind: "completion",
    context: "Order submitted",
    sectionName: "Confirmation",
  }),
});

export const TREATMENT_RUNTIME_NOTICE = Object.freeze({
  signIn: Object.freeze({
    title: "Please sign in again to continue",
    description:
      "Sign in with your account to pick up your saved treatment progress where you left off.",
    action: "Sign in",
  }),
  differentAccount: Object.freeze({
    title: "This saved intake belongs to a different account",
    description:
      "You signed in, but this saved treatment was started with another account. Sign in with the account you started with, or start over to begin a new intake.",
    action: "Sign in",
  }),
  rateLimited: Object.freeze({
    title: "Please wait a moment",
    description: "You've reached a temporary request limit. Wait, then try again. You don't need to start over.",
    action: "Try again",
  }),
  integrity: Object.freeze({
    title: "This treatment is unavailable right now",
    description:
      "We couldn't load this treatment. Please contact your care team if this continues.",
    action: "Check again",
  }),
  expired: Object.freeze({
    title: "This treatment session expired",
    description:
      "Start a fresh session so the latest immutable release and availability rules can be loaded.",
    action: "Start again",
  }),
  stale: Object.freeze({
    title: "A newer session state is available",
    description:
      "Reload the server-owned state before continuing. Your accepted answers remain protected from stale writes.",
    action: "Reload state",
  }),
  snapshot: Object.freeze({
    title: "This preview is no longer current",
    description:
      "Close this window and open Preview again from the portal to receive a capability for the current immutable release.",
    action: "Try again",
  }),
  // A failure that is not a service outage (4xx, or a client-side state error with
  // no HTTP status). The "unavailable" wording below is only true for 5xx/network.
  generic: Object.freeze({
    title: "We couldn't continue this treatment",
    description:
      "Something went wrong with your saved progress. Try again, or start over to begin fresh. If this keeps happening, please contact your care team.",
    action: "Try again",
  }),
  // The same, for an HTTP 4xx: repeating the request cannot help, so no retry is suggested.
  genericClientError: Object.freeze({
    title: "We couldn't continue this treatment",
    description:
      "Something went wrong with your saved progress. Start over to begin fresh. If this keeps happening, please contact your care team.",
    action: "Start over",
  }),
  unavailable: Object.freeze({
    title: "The treatment journey could not load",
    description:
      "The release or runtime service is temporarily unavailable. Retry without changing the configured journey.",
    action: "Try again",
  }),
  // Saved progress the page itself cannot use. Same title as `generic`, but
  // retrying cannot change it, so the copy points only at starting over and
  // the notice shows no Try again (only the start-over button the error carries).
  savedProgress: Object.freeze({
    title: "We couldn't continue this treatment",
    description:
      "We couldn't restore your saved progress. Start over to begin this treatment again. If this keeps happening, please contact your care team.",
    action: null,
  }),
});

export const TREATMENT_RECOVERY_STATE = Object.freeze({
  authorizationFailed: "authorization_failed",
  authorizationPending: "authorization_pending",
  authorized: "authorized",
  providerStarted: "provider_started",
});

export const TREATMENT_RECOVERY_STATUS = Object.freeze({
  failed: "failed",
  recoveryRequired: "recovery_required",
  authorized: "authorized",
  complete: "complete",
});

export const TREATMENT_RECOVERY_COPY = Object.freeze({
  authorizationFailed: Object.freeze({
    eyebrow: "Payment was not authorized",
    title: "Choose a safe way to continue",
    description:
      "Your answers and accepted quote are saved. No provider fulfillment was started.",
  }),
  authorizationPending: Object.freeze({
    eyebrow: "Payment confirmation pending",
    title: "We are confirming your authorization",
    description:
      "Do not submit payment again. Your checkout is saved while the existing authorization is reconciled.",
  }),
  authorized: Object.freeze({
    eyebrow: "Authorization recorded",
    title: "Support is reviewing this checkout",
    description:
      "Payment has an existing authorization. Pricing and selections are locked to prevent duplicate financial effects.",
  }),
  providerStarted: Object.freeze({
    eyebrow: "Clinical fulfillment started",
    title: "Support owns the next step",
    description:
      "This checkout can no longer be edited safely because provider fulfillment has started.",
  }),
  default: Object.freeze({
    eyebrow: "Checkout saved",
    title: "Choose how you would like to continue",
    description:
      "Your questionnaire answers and confirmed quote are preserved. Nothing has been submitted again.",
  }),
  supportReference: "Support reference",
  attemptReference: "Checkout reference",
  recoveryStatus: "Recovery status",
});
